import { Buffer } from "buffer";
import { countries } from "./countries";
import MediaFormats from "types/MediaFormats";
import QuizTypes from "types/QuizTypes";
import i18n from "i18n";
import i18next from "i18next";
import IOption from "models/Option";
import AiProviderTypes from "types/AiProviderTypes";

export const decodeBase64 = (hash:string) => {
  const hasToString = Buffer.from(hash, 'base64').toString();
  return JSON.parse(hasToString);
}

export const encodeBase64 = (data:any) => {
  const dataToString = JSON.stringify(data);
  return Buffer.from(dataToString).toString('base64');
}

export const textFromCamelToNormalCase = (text:string) => {
  return text.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, (l:string) => l.toUpperCase());
}

export const getCountryName = (code: string) => {
  if (!code) return 'Unknown';
  const c = code.toLowerCase();
  const countryName = countries[c];
  if (!countryName) return code.toUpperCase();
  return countryName;
}

export const expandAccordion = (name: string) => {
  const element = document.querySelector(`[react-scroll="${name}"]`);
  if (!element) return;

  const accordionElement:any = element.querySelector('.MuiAccordionSummary-root');
  if (accordionElement) {
    const isExpanded = accordionElement.getAttribute('aria-expanded') === 'true';

    if (!isExpanded) {
      setTimeout(() => accordionElement.click(), 50);
      setTimeout(() => {
        element.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  } else {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};

export const mediasInfo:Record<MediaFormats, {width: number, height: number, label: string, hint: string }> = {
  [MediaFormats.CTA]: { width: 420, height: 350, label: 'CTA', hint: i18n.t('form.helperText.media.cta') },
  [MediaFormats.Interstitial]: { width: 420, height: 350, label: 'Interstitial', hint: i18n.t('form.helperText.media.interstitial') },
  [MediaFormats.Logo]: { width: 120, height: 120, label: 'Square Logo', hint: i18n.t('form.helperText.media.logo') },
  [MediaFormats.RectLogo]: { width: 240, height: 120, label: 'Rectangular Logo', hint: i18n.t('form.helperText.media.rectLogo') },
  [MediaFormats.Question]: { width: 420, height: 236, label: 'Question', hint: i18n.t('form.helperText.media.question') },
  [MediaFormats.SocialShare]: { width: 420, height: 350, label: 'Social Share', hint: i18n.t('form.helperText.media.socialShare') },
  [MediaFormats.Wallpaper]: { width: 1920, height: 1080, label: 'Live Results Wallpaper', hint: i18n.t('form.helperText.media.wallpaper') },
  [MediaFormats.PortraitWallpaper]: { width: 1080, height: 1920, label: 'Live Results Portrait Wallpaper', hint: i18n.t('form.helperText.media.portraitWallpaper') },
  [MediaFormats.GameWallpaper]: { width: 1920, height: 1080, label: 'Game Wallpaper', hint: i18n.t('form.helperText.media.gameWallpaper') },
  [MediaFormats.Background]: { width: 414, height: 896, label: 'Background', hint: i18n.t('form.helperText.media.background') },
  [MediaFormats.HomeScreen]: { width: 240, height: 240, label: 'Home Screen', hint: i18n.t('form.helperText.media.homeScreen') },
  [MediaFormats.GridImage]: { width: 120, height: 120, label: 'Grid Image', hint: i18n.t('form.helperText.media.gridImage') },
};

export type AiImageStyle = 'flat' | 'gradient' | 'outline' | '3d' | 'minimal';
export type AiImageShape = 'square' | 'rounded' | 'circle';

// `label` is localized for display only. The AI-facing prompt always uses the raw enum value / hex code
// (see buildEnrichedAiImagePrompt) so the instructions the model receives stay exact and locale-independent.
export const aiImageStyleOptions:{ value: AiImageStyle, label: string }[] = [
  { value: 'flat', label: i18n.t('dialogs.generateImage.styleFlat') },
  { value: 'gradient', label: i18n.t('dialogs.generateImage.styleGradient') },
  { value: 'outline', label: i18n.t('dialogs.generateImage.styleOutline') },
  { value: '3d', label: i18n.t('dialogs.generateImage.style3d') },
  { value: 'minimal', label: i18n.t('dialogs.generateImage.styleMinimal') },
];

export const aiImageColorOptions:{ value: string, label: string }[] = [
  { value: '#3B82F6', label: i18n.t('dialogs.generateImage.colorBlue') },
  { value: '#22C55E', label: i18n.t('dialogs.generateImage.colorGreen') },
  { value: '#F97316', label: i18n.t('dialogs.generateImage.colorOrange') },
  { value: '#8B5CF6', label: i18n.t('dialogs.generateImage.colorPurple') },
  { value: '#EF4444', label: i18n.t('dialogs.generateImage.colorRed') },
];

export const aiImageShapeOptions:{ value: AiImageShape, label: string }[] = [
  { value: 'square', label: i18n.t('dialogs.generateImage.shapeSquare') },
  { value: 'rounded', label: i18n.t('dialogs.generateImage.shapeRounded') },
  { value: 'circle', label: i18n.t('dialogs.generateImage.shapeCircle') },
];

// Only icon-like formats can be meaningfully framed as square/rounded/circle.
const formatsWithShapeOption:MediaFormats[] = [MediaFormats.Logo, MediaFormats.RectLogo, MediaFormats.HomeScreen, MediaFormats.GridImage];
export const supportsAiImageShapeOption = (format:MediaFormats) => formatsWithShapeOption.includes(format);

// Fixed English words for the AI-facing prompt — kept separate from the translated UI `label` fields above.
const aiImageStyleWords:Record<AiImageStyle, string> = {
  flat: 'Flat', gradient: 'Gradient', outline: 'Outline', '3d': '3D', minimal: 'Minimal',
};

const aiImageShapeWords:Record<AiImageShape, string> = {
  square: 'Square', rounded: 'Rounded', circle: 'Circle',
};

// Builds a structured "Label: Value" instruction block per selected option (rather than prepending free-form
// text), so the AI backend receives explicit, unambiguous direction. Colors are passed as exact hex codes
// rather than color names, since a named color (e.g. "purple") can map to many different hues.
export const buildEnrichedAiImagePrompt = (basePrompt:string, style:AiImageStyle | null, colorHex:string | null, shape:AiImageShape | null):string => {
  const trimmedBase = basePrompt.trim();

  const lines:string[] = [];
  if (style) lines.push(`Style: ${aiImageStyleWords[style]}`);
  if (colorHex) lines.push(`Color: ${colorHex}`);
  if (shape) lines.push(`Shape: ${aiImageShapeWords[shape]}`);

  if (!lines.length) return trimmedBase;
  return `${trimmedBase}\n\n${lines.join('\n')}`;
}

export const quizTypesLabels:Record<QuizTypes, string> = {
  [QuizTypes.Personality]: i18n.t('pages.quizForm.personalityLabel'),
  [QuizTypes.Poll]: i18n.t('pages.quizForm.pollLabel'),
  [QuizTypes.Prediction]: i18n.t('pages.quizForm.predictionLabel'),
  [QuizTypes.Scoring]: i18n.t('pages.quizForm.scoringLabel'),
  [QuizTypes.Survey]: i18n.t('pages.quizForm.surveyLabel'),
  [QuizTypes.RankingPoll]: i18n.t('pages.quizForm.rankingPollLabel'),
  [QuizTypes.TeamSelector]: i18n.t('pages.quizForm.teamSelectorLabel'),
  [QuizTypes.BeatTheExpert]: i18n.t('pages.quizForm.beatTheExpertLabel'),
  [QuizTypes.Streak]: i18n.t('pages.quizForm.streakLabel'),
}

export const sponsorLogoPagesOptions = (quizType: QuizTypes) => {
  const isScoringQuizzes = [QuizTypes.Scoring, QuizTypes.Prediction, QuizTypes.BeatTheExpert, QuizTypes.Streak].includes(quizType as QuizTypes);
  const isPollQuizzes = [QuizTypes.Poll, QuizTypes.RankingPoll].includes(quizType);
  const isSurveyQuiz = quizType === QuizTypes.Survey;
  const isPersonalityQuiz = quizType === QuizTypes.Personality;

  const options = [
    { label: 'Waiver', value: 'waiver' },
    { label: 'Countdown', value: 'countdown' },
  ];

  if (!isSurveyQuiz) options.push({ label: 'Question', value: 'question' });
  if (!isSurveyQuiz) options.push({ label: 'Timer', value: 'timer' });
  options.push({ label: 'Media', value: 'media' });
  options.push({ label: 'Survey questions', value: 'additionalQuestions' });
  options.push({ label: 'Register', value: 'register' });
  if (!isPersonalityQuiz) options.push({ label: 'CTA Screen', value: 'post-quiz' })
  if (isScoringQuizzes) options.push({ label: 'Leaderboard', value: 'leaderboard' })
  if (isPollQuizzes) options.push({ label: 'Results', value: 'results' })

  return options;
}

function updateTranslations () {
  mediasInfo[MediaFormats.CTA].hint = i18n.t('form.helperText.media.cta');
  mediasInfo[MediaFormats.Interstitial].hint = i18n.t('form.helperText.media.interstitial');
  mediasInfo[MediaFormats.Logo].hint = i18n.t('form.helperText.media.logo');
  mediasInfo[MediaFormats.Question].hint = i18n.t('form.helperText.media.question');
  mediasInfo[MediaFormats.SocialShare].hint = i18n.t('form.helperText.media.socialShare');
  mediasInfo[MediaFormats.Wallpaper].hint = i18n.t('form.helperText.media.wallpaper');
  mediasInfo[MediaFormats.PortraitWallpaper].hint = i18n.t('form.helperText.media.portraitWallpaper');
  mediasInfo[MediaFormats.GameWallpaper].hint = i18n.t('form.helperText.media.gameWallpaper');
  mediasInfo[MediaFormats.Background].hint = i18n.t('form.helperText.media.background');
  mediasInfo[MediaFormats.HomeScreen].hint = i18n.t('form.helperText.media.homeScreen');
  mediasInfo[MediaFormats.GridImage].hint = i18n.t('form.helperText.media.gridImage');

  quizTypesLabels[QuizTypes.Personality] = i18n.t('pages.quizForm.personalityLabel');
  quizTypesLabels[QuizTypes.Poll] = i18n.t('pages.quizForm.pollLabel');
  quizTypesLabels[QuizTypes.Prediction] = i18n.t('pages.quizForm.predictionLabel');
  quizTypesLabels[QuizTypes.Scoring] = i18n.t('pages.quizForm.scoringLabel');
  quizTypesLabels[QuizTypes.Survey] = i18n.t('pages.quizForm.surveyLabel');
  quizTypesLabels[QuizTypes.RankingPoll] = i18n.t('pages.quizForm.rankingPollLabel');
  quizTypesLabels[QuizTypes.TeamSelector] = i18n.t('pages.quizForm.teamSelectorLabel');
  quizTypesLabels[QuizTypes.BeatTheExpert] = i18n.t('pages.quizForm.beatTheExpertLabel');
  quizTypesLabels[QuizTypes.Streak] = i18n.t('pages.quizForm.streakLabel');

  aiImageStyleOptions[0].label = i18n.t('dialogs.generateImage.styleFlat');
  aiImageStyleOptions[1].label = i18n.t('dialogs.generateImage.styleGradient');
  aiImageStyleOptions[2].label = i18n.t('dialogs.generateImage.styleOutline');
  aiImageStyleOptions[3].label = i18n.t('dialogs.generateImage.style3d');
  aiImageStyleOptions[4].label = i18n.t('dialogs.generateImage.styleMinimal');

  aiImageColorOptions[0].label = i18n.t('dialogs.generateImage.colorBlue');
  aiImageColorOptions[1].label = i18n.t('dialogs.generateImage.colorGreen');
  aiImageColorOptions[2].label = i18n.t('dialogs.generateImage.colorOrange');
  aiImageColorOptions[3].label = i18n.t('dialogs.generateImage.colorPurple');
  aiImageColorOptions[4].label = i18n.t('dialogs.generateImage.colorRed');

  aiImageShapeOptions[0].label = i18n.t('dialogs.generateImage.shapeSquare');
  aiImageShapeOptions[1].label = i18n.t('dialogs.generateImage.shapeRounded');
  aiImageShapeOptions[2].label = i18n.t('dialogs.generateImage.shapeCircle');
}

if (i18n.isInitialized) {
  updateTranslations();
}

i18next.on('languageChanged', function(lng) {
  updateTranslations();
});

export const downloadQRCode = (name: string) => {
  const canvas = document.querySelector("#qrcode-canvas") as HTMLCanvasElement;
  if (!canvas) return;
  
  const pngUrl = canvas
    .toDataURL("image/png")
    .replace("image/png", "image/octet-stream");
  const downloadLink = document.createElement("a");
  downloadLink.href = pngUrl;
  downloadLink.download = `${name.replace(/\s/g, '-')}-Leaderboard-QR-Code.png`;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
}

export const getOptionsFromEnum = (Enum: any) => {
  return (Object.keys(Enum) as Array<keyof typeof Enum>).map(key => ({value: Enum[key], label: textFromCamelToNormalCase(key as string) })) as IOption[];
}

export const aiProviderLabel:Record<AiProviderTypes, string> = {
  [AiProviderTypes.OpenAi]: 'OpenAI',
  [AiProviderTypes.Perplexity]: 'Perplexity',
}

type RolePosition = {
  role: string;
  label: string;
  x: number;
  y: number;
}

export const teamSelectorFormations:Record<string, RolePosition[]> = {
  '4-4-2': [
    { role: 'GK', label: 'Goalkeeper', x: 50, y: 90 },

    { role: 'RB', label: 'Right Back', x: 80, y: 65 },
    { role: 'CB1', label: 'Right Center Back', x: 65, y: 75 },
    { role: 'CB2', label: 'Left Center Back', x: 35, y: 75 },
    { role: 'LB', label: 'Left Back', x: 20, y: 65 },

    { role: 'RM', label: 'Right Midfielder', x: 80, y: 40 },
    { role: 'CM1', label: 'Right Central Midfielder', x: 65, y: 50 },
    { role: 'CM2', label: 'Left Central Midfielder', x: 35, y: 50 },
    { role: 'LM', label: 'Left Midfielder', x: 20, y: 40 },

    { role: 'ST1', label: 'First Striker', x: 70, y: 20 },
    { role: 'ST2', label: 'Second Striker', x: 30, y: 20 },
  ],

  '4-5-1': [
    { role: 'GK', label: 'Goalkeeper', x: 50, y: 90 },

    { role: 'RB', label: 'Right Back', x: 80, y: 65 },
    { role: 'CB1', label: 'Right Center Back', x: 65, y: 75 },
    { role: 'CB2', label: 'Left Center Back', x: 35, y: 75 },
    { role: 'LB', label: 'Left Back', x: 20, y: 65 },

    { role: 'RM', label: 'Right Midfielder', x: 80, y: 40 },
    { role: 'CM1', label: 'Right Central Midfielder', x: 65, y: 50 },
    { role: 'CM2', label: 'Left Central Midfielder', x: 35, y: 50 },
    { role: 'CAM', label: 'Attacking Midfielder', x: 50, y: 35 },
    { role: 'LM', label: 'Left Midfielder', x: 20, y: 40 },

    { role: 'ST', label: 'Striker', x: 50, y: 20 },
  ],

  '3-4-3': [
    { role: 'GK', label: 'Goalkeeper', x: 50, y: 90 },

    { role: 'CB1', label: 'Right Center Back', x: 70, y: 65 },
    { role: 'CB2', label: 'Middle Center Back', x: 50, y: 75 },
    { role: 'CB3', label: 'Left Center Back', x: 30, y: 65 },

    { role: 'RM', label: 'Right Midfielder', x: 80, y: 50 },
    { role: 'CM1', label: 'Right Central Midfielder', x: 65, y: 40 },
    { role: 'CM2', label: 'Left Central Midfielder', x: 35, y: 40 },
    { role: 'LM', label: 'Left Midfielder', x: 20, y: 50 },

    { role: 'RW', label: 'Right Winger', x: 75, y: 25 },
    { role: 'ST', label: 'Striker', x: 50, y: 15 },
    { role: 'LW', label: 'Left Winger', x: 25, y: 25 },
  ]
};